import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { learningApi } from '../api/learningApi'
import { useAuth } from '@/core/auth/AuthContext'
import LoadingPage from '@/shared/components/LoadingPage'

type SortKey = 'name' | 'total' | 'verified' | 'pending' | 'certifications' | 'last_activity'
type SortDir = 'asc' | 'desc'

const LearningByEmployeePage = () => {
  const { user } = useAuth()
  const [search, setSearch]       = useState('')
  const [department, setDept]     = useState('')
  const [sortKey, setSortKey]     = useState<SortKey>('name')
  const [sortDir, setSortDir]     = useState<SortDir>('asc')
  const [showNoRecords, setShowNoRecords] = useState(true)

  const isAdminHr =
    user?.roles?.some(r => ['admin', 'hr'].includes(r.name.toLowerCase())) ||
    user?.division_name?.toLowerCase().includes('human')

  const { data, isLoading } = useQuery({
    queryKey: ['employeesOverview'],
    queryFn: () => learningApi.getEmployeesOverview(),
    enabled: !!isAdminHr,
  })

  const employees = data?.data ?? []

  const departments = useMemo(() => {
    const depts = [...new Set(employees.map(e => e.department).filter(Boolean))].sort()
    return depts
  }, [employees])

  const filtered = useMemo(() => {
    let rows = employees

    if (!showNoRecords) rows = rows.filter(e => e.total_records > 0)

    if (department) rows = rows.filter(e => e.department === department)

    if (search) {
      const q = search.toLowerCase()
      rows = rows.filter(e =>
        e.full_name.toLowerCase().includes(q) ||
        e.email.toLowerCase().includes(q) ||
        e.department.toLowerCase().includes(q)
      )
    }

    return [...rows].sort((a, b) => {
      let cmp = 0
      switch (sortKey) {
        case 'name':          cmp = a.full_name.localeCompare(b.full_name); break
        case 'total':         cmp = a.total_records - b.total_records; break
        case 'verified':      cmp = a.verified_count - b.verified_count; break
        case 'pending':       cmp = a.pending_count - b.pending_count; break
        case 'certifications':cmp = a.certification_count - b.certification_count; break
        case 'last_activity': cmp = (a.last_activity ?? '').localeCompare(b.last_activity ?? ''); break
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [employees, search, department, sortKey, sortDir, showNoRecords])

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(key); setSortDir('desc') }
  }

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <span className="text-gray-300 ml-1">↕</span>
    return <span className="text-red-500 ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>
  }

  const pendingTotal   = employees.reduce((s, e) => s + e.pending_count, 0)
  const noRecordsCount = employees.filter(e => e.total_records === 0).length

  if (!isAdminHr) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You do not have permission to view this page.</p>
          <Link to="/learning" className="mt-4 inline-block text-red-600 hover:text-red-700">&larr; Back</Link>
        </div>
      </div>
    )
  }

  if (isLoading) return <LoadingPage />

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-rose-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <nav className="flex items-center space-x-2 text-sm mb-3">
            <Link to="/learning" className="text-red-100 hover:text-white">Learning</Link>
            <span className="text-red-300">/</span>
            <span className="text-white">By Employee</span>
          </nav>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold">Training by Employee</h1>
              <p className="text-red-100 mt-1 text-sm">
                {employees.length} employees
                {noRecordsCount > 0 && ` · ${noRecordsCount} with no records`}
                {pendingTotal > 0 && ` · ${pendingTotal} pending verification`}
              </p>
            </div>
            <Link
              to="/learning/all-records"
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              All Training Records
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-5">
        {/* Summary chips */}
        <div className="flex flex-wrap gap-3">
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 text-center min-w-[110px]">
            <p className="text-2xl font-bold text-gray-900">{employees.length}</p>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-0.5">Employees</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 text-center min-w-[110px]">
            <p className="text-2xl font-bold text-green-700">{employees.filter(e => e.total_records > 0).length}</p>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-0.5">With Records</p>
          </div>
          {noRecordsCount > 0 && (
            <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-center min-w-[110px]">
              <p className="text-2xl font-bold text-red-700">{noRecordsCount}</p>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-0.5">No Records</p>
            </div>
          )}
          {pendingTotal > 0 && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 text-center min-w-[110px]">
              <p className="text-2xl font-bold text-amber-700">{pendingTotal}</p>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-0.5">Pending Review</p>
            </div>
          )}
          <div className="bg-purple-50 border border-purple-100 rounded-xl px-4 py-3 text-center min-w-[110px]">
            <p className="text-2xl font-bold text-purple-700">{employees.reduce((s, e) => s + e.certification_count, 0)}</p>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-0.5">Certifications</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by name, email or department…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>
            <select
              value={department}
              onChange={e => setDept(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="">All Departments</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showNoRecords}
                onChange={e => setShowNoRecords(e.target.checked)}
                className="rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              Show employees with no records
            </label>
            {(search || department || !showNoRecords) && (
              <button
                onClick={() => { setSearch(''); setDept(''); setShowNoRecords(true) }}
                className="text-xs text-red-600 hover:text-red-800 font-medium"
              >
                Clear filters
              </button>
            )}
            <span className="text-xs text-gray-400 ml-auto">{filtered.length} shown</span>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/60">
                  <th
                    className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
                    onClick={() => toggleSort('name')}
                  >
                    Employee <SortIcon col="name" />
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Department
                  </th>
                  <th
                    className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
                    onClick={() => toggleSort('total')}
                  >
                    Total <SortIcon col="total" />
                  </th>
                  <th
                    className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
                    onClick={() => toggleSort('verified')}
                  >
                    Verified <SortIcon col="verified" />
                  </th>
                  <th
                    className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
                    onClick={() => toggleSort('pending')}
                  >
                    Pending <SortIcon col="pending" />
                  </th>
                  <th
                    className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
                    onClick={() => toggleSort('certifications')}
                  >
                    Certs <SortIcon col="certifications" />
                  </th>
                  <th
                    className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
                    onClick={() => toggleSort('last_activity')}
                  >
                    Last Activity <SortIcon col="last_activity" />
                  </th>
                  <th className="py-3 px-4" />
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-16 text-gray-400 text-sm">
                      No employees match the current filters.
                    </td>
                  </tr>
                )}
                {filtered.map(emp => (
                  <tr key={emp.id} className="border-b border-gray-100 hover:bg-gray-50 group">
                    <td className="py-3.5 px-4">
                      <Link
                        to={`/learning/employees/${emp.id}`}
                        state={{ employeeName: emp.full_name, department: emp.department, email: emp.email }}
                        className="flex items-center gap-2.5"
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                          emp.total_records === 0
                            ? 'bg-gray-100 text-gray-400'
                            : emp.pending_count > 0
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-red-100 text-red-600'
                        }`}>
                          {emp.full_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 group-hover:text-red-600">{emp.full_name}</p>
                          <p className="text-xs text-gray-400">{emp.email}</p>
                        </div>
                      </Link>
                    </td>

                    <td className="py-3.5 px-4 text-sm text-gray-600">
                      {emp.department || <span className="text-gray-300">—</span>}
                    </td>

                    <td className="py-3.5 px-4 text-center">
                      {emp.total_records === 0 ? (
                        <span className="text-xs text-gray-300">—</span>
                      ) : (
                        <span className="text-sm font-semibold text-gray-900">{emp.total_records}</span>
                      )}
                    </td>

                    <td className="py-3.5 px-4 text-center">
                      {emp.verified_count > 0 ? (
                        <span className="text-sm font-semibold text-green-700">{emp.verified_count}</span>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>

                    <td className="py-3.5 px-4 text-center">
                      {emp.pending_count > 0 ? (
                        <span className="inline-flex items-center justify-center min-w-[24px] px-1.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700">
                          {emp.pending_count}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>

                    <td className="py-3.5 px-4 text-center">
                      {emp.certification_count > 0 ? (
                        <span className="inline-flex items-center gap-1 text-sm font-semibold text-purple-700">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                          </svg>
                          {emp.certification_count}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>

                    <td className="py-3.5 px-4 text-sm text-gray-500 whitespace-nowrap">
                      {emp.last_activity
                        ? format(new Date(emp.last_activity), 'dd MMM yyyy')
                        : <span className="text-gray-300 text-xs">No activity</span>}
                    </td>

                    <td className="py-3.5 px-4 text-right">
                      <Link
                        to={`/learning/employees/${emp.id}`}
                        state={{ employeeName: emp.full_name, department: emp.department, email: emp.email }}
                        className="text-xs font-medium text-gray-500 hover:text-red-600 px-2.5 py-1 rounded-md hover:bg-red-50 transition-colors"
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LearningByEmployeePage
